import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToId, EASE } from "@/lib/scroll";

const HERO_IMG = "/images/hero-eating.webp";

const lineAnim = (i) => ({
  initial: { y: "112%" },
  animate: { y: "0%" },
  transition: { duration: 1.2, ease: EASE, delay: 0.25 + i * 0.16 },
});

const fadeAnim = (delay) => ({
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1, ease: EASE, delay },
});

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 180]);

  return (
    <section ref={ref} id="top" className="relative overflow-hidden">
      <motion.div
        style={{ y: glowY }}
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
      >
        <div className="absolute left-1/2 top-[-20%] h-[70vh] w-[90vw] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.13)_0%,rgba(17,17,17,0)_65%)]" />
      </motion.div>

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 pb-20 pt-32 lg:grid-cols-12 lg:gap-10 lg:px-10 lg:pb-28 lg:pt-44">
        <div className="lg:col-span-7">
          <motion.div {...fadeAnim(0.1)} className="mb-8 flex items-center gap-4">
            <span className="h-px w-12 bg-gold/70" />
            <p className="font-mono text-[10px] uppercase tracking-[0.38em] text-gold sm:text-[11px]">
              Hrană umedă super premium · România
            </p>
          </motion.div>

          <h1 className="font-serif text-[3.4rem] font-light leading-[0.98] tracking-tight text-cream sm:text-7xl lg:text-[6.6rem]">
            {[
              <span key="w1" className="block">Natură.</span>,
              <span key="w2" className="block italic text-gold-gradient">Iubire.</span>,
              <span key="w3" className="block">Grijă.</span>,
            ].map((word, i) => (
              <span key={i} className="block overflow-hidden pb-2">
                <motion.span className="block" {...lineAnim(i)}>
                  {word}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            {...fadeAnim(0.85)}
            className="mt-8 max-w-md text-base leading-relaxed text-stone sm:text-lg"
          >
            Hrană umedă super premium pentru pisici, fabricată în România.
          </motion.p>

          <motion.div {...fadeAnim(1)} className="mt-12 flex flex-wrap items-center gap-5">
            <button
              data-testid="hero-cta-contact"
              onClick={() => scrollToId("contact")}
              className="group relative overflow-hidden rounded-full bg-gold px-9 py-4 font-mono text-[11px] uppercase tracking-[0.24em] text-ink transition-colors duration-500 hover:bg-gold-light"
            >
              <span className="relative z-10">Contactează-ne</span>
            </button>
            <button
              data-testid="hero-cta-benefits"
              onClick={() => scrollToId("beneficii")}
              className="rounded-full border border-gold/30 px-9 py-4 font-mono text-[11px] uppercase tracking-[0.24em] text-gold transition-all duration-500 hover:border-gold hover:bg-gold/5"
            >
              Descoperă formula
            </button>
          </motion.div>

          <motion.div
            {...fadeAnim(1.15)}
            className="mt-16 flex flex-wrap items-center gap-x-8 gap-y-3"
          >
            {["85% Carne", "Fără cereale", "Borcan de sticlă"].map((t) => (
              <span
                key={t}
                className="flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.26em] text-stone"
              >
                <span className="h-1 w-1 rotate-45 bg-gold" />
                {t}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.4, ease: EASE, delay: 0.5 }}
            className="relative mx-auto max-w-sm lg:max-w-none"
          >
            <div
              className="absolute -inset-0 translate-x-5 translate-y-5 rounded-b-3xl rounded-t-full border border-gold/25"
              aria-hidden="true"
            />
            <motion.div style={{ y: imgY }} className="relative">
              <div className="relative aspect-[3/4] overflow-hidden rounded-b-3xl rounded-t-full border border-gold/40">
                <motion.img
                  initial={{ scale: 1.18 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 2, ease: EASE, delay: 0.5 }}
                  src={HERO_IMG}
                  alt="Pisică tabby lingându-se pe bot lângă farfuria cu hrană NOSA"
                  className="h-full w-full object-cover object-[72%_center]"
                  data-testid="hero-cat-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
              </div>

              <motion.div
                animate={{ y: [0, -12, 0] }}
                transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
                className="absolute -left-4 top-16 flex items-center gap-3 rounded-2xl border border-gold/30 bg-card/90 px-5 py-4 shadow-[0_18px_50px_rgba(0,0,0,0.55)] backdrop-blur-xl sm:-left-10"
                data-testid="hero-badge-protein"
              >
                <span className="font-serif text-3xl text-gold-light">85%</span>
                <span className="font-mono text-[9px] uppercase leading-relaxed tracking-[0.2em] text-stone">
                  Carne
                  <br />
                  de pui
                </span>
              </motion.div>

              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ repeat: Infinity, duration: 7, ease: "easeInOut", delay: 1 }}
                className="absolute -right-3 bottom-14 rounded-2xl border border-gold/30 bg-card/90 px-5 py-4 shadow-[0_18px_50px_rgba(0,0,0,0.55)] backdrop-blur-xl sm:-right-8"
                data-testid="hero-badge-grainfree"
              >
                <span className="font-mono text-[9px] uppercase leading-relaxed tracking-[0.2em] text-stone">
                  Fără
                  <br />
                  cereale
                </span>
                <span className="ml-3 inline-block h-1.5 w-1.5 rotate-45 bg-gold" />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        {...fadeAnim(1.4)}
        className="relative flex items-center gap-4 px-6 pb-14 lg:px-10"
      >
        <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-stone/70">
          Derulează
        </span>
        <span className="relative h-px w-24 overflow-hidden bg-gold/15">
          <motion.span
            animate={{ x: ["-100%", "100%"] }}
            transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
            className="absolute inset-y-0 w-1/2 bg-gold"
          />
        </span>
      </motion.div>
    </section>
  );
}
